/** @jsxImportSource @emotion/react */
import { useContext, useEffect, useMemo, useState, useCallback } from 'react'
import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import { Alert, Modal } from 'antd'

import BaseActionButton from '../../components/Page/BaseActionButton'
import { ScheduleReprocessingContext } from '../../components/ScheduleReprocessingContext'
import { t } from '@sportninja/common/i18n'
import colors from '@sportninja/common/constants/appColors'
import Text from 'src/components/ts-components/Text'
import Button from 'src/components/ts-components/Button'
import { TextAreaInput } from 'src/components/ts-components/TextAreaInput'
import useWindowSize from 'src/hooks/useWindowSize'
import { WarningOutlined } from '@ant-design/icons'
import { getErrorMessage } from '@sportninja/common/utils/utils'

const ReprocessStats = ({ id, onSuccess }) => {
  const { listeners, addPending } = useContext(ScheduleReprocessingContext)
  const [localLoading, setLocalLoading] = useState(false)
  const [isPending, setIsPending] = useState(!!listeners[id])
  const [isOpen, setIsOpen] = useState(false)

  // Reason input state
  const [reason, setReason] = useState('')
  const [fieldErrors, setFieldErrors] = useState([])
  const [customError, setCustomError] = useState({})
  const [error, setError] = useState(null)

  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])

  useEffect(() => {
    if (isPending && !listeners[id]) {
      onSuccess()
    }
  }, [listeners[id]])

  const title = 'Reprocess Statistics'

  // No select options needed when using only TextAreaInput

  const handleOpen = useCallback(() => {
    setIsOpen(true)
  }, [])

  const handleClose = useCallback(() => {
    setIsOpen(false)
    // Reset validation state but preserve the selected option so users can reopen quickly
    setFieldErrors([])
    setCustomError({})
    setReason('')
    setError(null)
  }, [])

  const handleReprocessClick = useCallback(async () => {
    setLocalLoading(true)
    setError(null)
    setCustomError({})
    setFieldErrors([])
    try {
      const payloadReason = reason?.trim() || undefined
      const params = payloadReason
        ? new URLSearchParams({ reprocess_reason: payloadReason }).toString()
        : ''
      await req(`/schedules/${id}/reprocess${params ? `?${params}` : ''}`)
      handleClose()
      addPending(id)
      setIsPending(true)
    } catch (err) {
      setIsPending(false)
      const errorMessage = getErrorMessage(err)
      setError(errorMessage)
    } finally {
      setLocalLoading(false)
    }
  }, [reason, id, addPending, handleClose])

  return (
    <>
      {/* Trigger button */}
      <BaseActionButton
        disabled={localLoading || listeners[id]}
        loading={localLoading || listeners[id]}
        name='sync'
        title={title}
        onClick={handleOpen}
      />

      {/* Modal styled similarly to DeleteConfirmationModal */}
      <Modal
        css={css`
          display: flex;
          flex: 1;
          height: ${isMobile ? '100vh' : 'auto'} !important;
          margin: ${isMobile ? '0' : '24px'};
          padding: 0;
          max-width: ${isMobile ? '100%' : '500px'};
          top: ${isMobile ? '0' : '50%'};
          .ant-modal-content {
            height: ${isMobile ? '100vh' : 'auto'} !important;
            border-radius: ${isMobile ? '0' : '16px'};
            display: flex;
            flex-direction: column;
            background: ${colors.SECONDARY_300};
            border: 1px solid ${colors.SECONDARY_100};
          }
          .ant-modal-body {
            flex: 1;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            padding: 24px;
          }
          .ant-modal-header {
            display: none;
          }
        `}
        width={isMobile ? '100%' : 500}
        open={isOpen}
        onCancel={handleClose}
        centered={!isMobile}
        maskClosable={false}
        footer={null}
        styles={{
          content: {
            backgroundColor: colors.SECONDARY_400,
            padding: 0,
            borderWidth: 1,
            borderColor: colors.SECONDARY_300,
          },
          wrapper: {
            zIndex: 10002,
          },
          mask: {
            backdropFilter: 'blur(2px)',
            backgroundColor: 'rgba(6, 10, 16, 0.8)',
            zIndex: 10001,
          },
        }}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-items: center;
            gap: 16px;
            position: relative;
          `}
        >
          {/* Warning Icon */}
          <WarningOutlined
            css={css`
              font-size: 48px;
              color: ${colors.ERROR_200};
            `}
          />

          {/* Title */}
          <Text
            variant='heading3'
            weight='semiBold'
            color={colors.WHITE}
            style={`
              text-align: center;
            `}
          >
            Reprocess Statistics
          </Text>

          {/* Description */}
          <div
            css={css`
              align-self: stretch;
              text-align: center;
            `}
          >
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.WHITE}
            >
              Use this if stats appear incorrect or misaligned. <br />
              It will recalculate all game data and rebuild totals from scratch.
            </Text>
          </div>

          {/* Divider */}
          <div
            css={css`
              align-self: stretch;
              height: 1px;
              background: ${colors.SECONDARY_100};
            `}
          />

          {/* Form section */}
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-self: stretch;
              gap: 16px;
            `}
          >
            <TextAreaInput
              name='reason'
              value={reason}
              setValue={(v) => setReason(v)}
              fieldErrors={fieldErrors}
              customError={customError}
              label='Describe the Reason'
              placeholder='Enter Details'
              maxLength={255}
              disabled={localLoading}
              rows={1}
              autoSize={{ minRows: 1, maxRows: 4 }}
            />
          </div>

          {/* Note box */}
          <div
            css={css`
              align-self: stretch;
              padding: 8px 16px;
              background: ${colors.SECONDARY_300};
              border-radius: 8px;
              border: 1px solid ${colors.SECONDARY_100};
            `}
          >
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.WHITE}
            >
              <span
                css={css`
                  font-weight: 700;
                `}
              >
                Note:
              </span>{' '}
              Depending On The Number Of Games, This Process May Take Several
              Minutes To Complete. If Issues Persist, Contact{' '}
              <a
                href='mailto:support@sportninja.com'
                target='_blank'
                rel='noopener noreferrer'
                css={css`
                  color: ${colors.PRIMARY};
                  &:hover {
                    opacity: 0.8;
                  }
                `}
              >
                support@sportninja.com
              </a>
            </Text>
          </div>

          {/* Divider */}
          <div
            css={css`
              align-self: stretch;
              height: 1px;
              background: ${colors.SECONDARY_100};
            `}
          />

          {/* Error message */}
          {error && (
            <Alert
              showIcon
              message={error}
              type='error'
              css={css`
                align-self: stretch;
                justify-content: center;
                display: inline-flex;
              `}
            />
          )}

          {/* Action buttons */}
          <div
            css={css`
              align-self: stretch;
              justify-content: center;
              align-items: flex-start;
              gap: 16px;
              display: inline-flex;
            `}
          >
            <Button
              text='CANCEL'
              onClick={handleClose}
              disabled={localLoading}
              variant='secondary'
            />

            <Button
              text='REPROCESS'
              onClick={handleReprocessClick}
              disabled={localLoading || !reason || reason?.trim().length === 0}
              isLoading={localLoading || listeners[id]}
              variant='danger'
            />
          </div>
        </div>
      </Modal>
    </>
  )
}

export default ReprocessStats
